<template>
  <section class="hero" :class="{ 'hero--content': content }" data-item="Hero">
    <div class="hero-image">
      <slot name="image">
        <ElementsImage v-if="image" :src="image" alt="Hero Background" loading="eager" />
      </slot>
    </div>

    <div v-if="content" class="hero-container container">
      <div class="hero-content">
        <div v-if="title || $slots.title" class="hero-content__title">
          <slot name="title">{{ title }}</slot>
        </div>
        <div v-if="description || $slots.description" class="hero-content__description">
          <slot name="description">{{ description }}</slot>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  withDefaults(
    defineProps<{
      image?: any
      content?: boolean
      title?: string
      description?: string
    }>(),
    {
      content: true
    }
  )
</script>

<style lang="scss" scoped>
  .hero {
    @apply flex items-center justify-center relative;

    &-image {
      @apply w-full h-auto z-0;

      :deep(img) {
        @apply w-full h-auto object-cover object-center max-h-175;
      }
    }
    &-container {
      @apply z-20 absolute inset-0 w-full h-full flex;
    }
    &-content {
      @apply flex flex-col mt-auto mx-auto text-center gap-1 py-26 w-full;

      &__title {
        @apply font-display text-white/90 text-12 leading-normal font-semibold;
      }
      &__description {
        @apply flex items-center justify-center text-center gap-4 text-white/80 text-6 leading-normal text-pretty;
      }
    }

    &::before {
      @apply content-empty w-full h-full inset-0 absolute z-10;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0) 90%);
    }

    &--content {
      &::before {
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.8) 90%);
      }
      &::after {
        @apply content-empty h-25 -bottom-12.5 w-0.5 bg-[#E49E48] flex absolute z-15;
      }
    }

    @include media("xl") {
      &-content {
        @apply gap-0.75 py-22;

        &__title {
          @apply text-9;
        }
        &__description {
          @apply text-5;
        }
      }
    }
    @include media("md") {
      &-content {
        @apply gap-0.5 py-14;

        &__title {
          @apply text-6.5;
        }
        &__description {
          @apply text-4 gap-2.5;

          :deep(svg) {
            @apply w-3.5 h-3.5;
          }
        }
      }
      &--content {
        &::after {
          @apply h-12 -bottom-6;
        }
      }
    }
    @include media("sm") {
      &-image {
        :deep(img) {
          @apply h-60;
        }
      }
      &-content {
        @apply gap-0.5 py-6;

        &__title {
          @apply text-6 leading-8;
        }
      }
      &--content {
        &::after {
          @apply h-7 -bottom-3;
        }
      }
    }
  }
</style>
